import { inject } from '@angular/core';
import { Routes, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { UserManagementComponent } from './base/user-management/user-management.component';
import { LoginComponent } from './base/login/login.component';
import { ChangePasswordComponent } from './base/change-password/change-password.component';

import { HomeComponent } from './domain/home/home.component';
import { AuthGuard } from './base/guards/auth.guard';
import * as pages from './core/routing/page-names';
import { EditProfileComponent } from './base/edit-profile/edit-profile.component';
import { SetPasswordComponent } from './base/set-password/set-password.component';
import { ForgotPasswordComponent } from './base/forgot-password/forgot-password.component';
import { ClientService } from './base/services/client/client.service';
import { AdminOrClientAdminGuard } from './base/guards/admin-client-admin.guard';
import { ClientAdministrationComponent } from './domain/administration/client-administration/client-administration.component';
import { AdminGuard } from './base/guards/admin.guard';
import { DynamicClientAdministrationComponent } from './domain/administration/dynamic-client-administration/dynamic-client-administration.component';
import { DynamicUserManagementComponent } from './base/user-management-dynamic/dynamic-user-management.component';
import { ClientAdministrationService } from './domain/administration/client-administration/client-administration.service';
import { ThemeShowcaseComponent } from './domain/administration/theme-showcase/theme-showcase.component';
import { RunningTotalsComponent } from './domain/analytics/running-totals/running-totals.component';
import { TripOverviewComponent } from './domain/analytics/trip-overview/trip-overview.component';
import { StatisticEventsComponent } from './domain/analytics/statistic-events/statistic-events.component';
import { DiagnosticEventsComponent } from './domain/analytics/diagnostic-events/diagnostic-events.component';
import { PeriodicStatisticsComponent } from './domain/analytics/periodic-statistics/periodic-statistics.component';
import { SamplingProfilesComponent } from './domain/sampling-profiles/sampling-profiles.component';
import { VehiclesComponent } from './domain/vehicles/vehicles.component';
import { unsavedChangesGuard } from './base/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: pages.homePageName
  },
  {
    path: pages.homePageName,
    component: HomeComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.userManagementPageName,
    component: UserManagementComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
  },
  { // TODO: Remove when dynamic user management is implemented
    path: pages.dynamicUserManagementPageName,
    component: DynamicUserManagementComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
    providers: [ClientAdministrationService],
  },
  {
    path: pages.clientAdministrationPageName,
    component: ClientAdministrationComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminGuard).canActivate(route, state)],
  },
  { // TODO: Remove when dynamic client administration is implemented
    path: pages.dynamicClientAdministrationPageName,
    component: DynamicClientAdministrationComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminGuard).canActivate(route, state)],
  },
  {
    // Collector configuration. Admin-or-ClientAdmin matches the backend's Roles_AdminOrClientAdmin
    // on the SamplingProfile write endpoints.
    path: pages.samplingProfilesPageName,
    component: SamplingProfilesComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
  },
  {
    // Vehicle-first entry point for reassigning sampling profiles. Admin-or-ClientAdmin matches the
    // backend's Roles_AdminOrClientAdmin on SearchVehicles/AssignProfileToVehicle.
    path: pages.vehiclesPageName,
    component: VehiclesComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
  },
  {
    path: pages.themeShowcasePageName,
    component: ThemeShowcaseComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AdminOrClientAdminGuard).canActivate(route, state)],
    canDeactivate: [unsavedChangesGuard]
  },  {
    path: pages.runningTotalsPageName,
    component: RunningTotalsComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.tripOverviewPageName,
    component: TripOverviewComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.statisticEventsPageName,
    component: StatisticEventsComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.diagnosticEventsPageName,
    component: DiagnosticEventsComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.periodicStatisticsPageName,
    component: PeriodicStatisticsComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },  {
    path: pages.loginPageName,
    component: LoginComponent
  },
  {
    path: pages.changePasswordPageName,
    component: ChangePasswordComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.editProfilePageName,
    component: EditProfileComponent,
    canActivate: [(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)]
  },
  {
    path: pages.setPasswordPageName,
    component: SetPasswordComponent
  },
  {
    path: pages.forgotPasswordPageName,
    component: ForgotPasswordComponent
  }
];
